/**
 * Pure helpers for the gamification leaderboard and level badges.
 */
import * as React from 'react';
import { Medal, Trophy, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type {
  GamificationLevelBadge,
  GamificationLevelBadgeTier,
  PodiumRank,
} from '@/types/gamification.types';
import {
  ESTABLISHMENT_ICON_BOX_CLASS,
  ESTABLISHMENT_ICON_CLASS,
  GAMIFICATION_BADGE_ASSETS,
  GAMIFICATION_TIER_LABELS,
  LEADERBOARD_ROW_CLASS,
  LEADERBOARD_ROW_HIGHLIGHT_CLASS,
  LEADERBOARD_SEARCH_DEBOUNCE_MS,
  PODIUM_COLUMN_CLASS,
  PODIUM_ORDER,
} from '@/components/gamification/gamification-configs';

/** Returns `value` after it has stayed unchanged for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs = LEADERBOARD_SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** True when `icon` is an http(s) URL rather than a local asset key. */
export function isRemoteIconUrl(icon: string | null | undefined): icon is string {
  if (!icon || typeof icon !== 'string') return false;
  const trimmed = icon.trim().toLowerCase();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/** Map XP level number 1/2/3 to bronze / silver / gold. */
export function tierFromLevelNumber(
  levelNumber: number | null | undefined,
): GamificationLevelBadgeTier | null {
  if (levelNumber == null || !Number.isFinite(levelNumber)) return null;
  const level = Math.trunc(levelNumber);
  if (level === 1) return 'bronze';
  if (level === 2) return 'silver';
  if (level === 3) return 'gold';
  return null;
}

/** Bundled PNG src for a badge tier. */
export function badgeImageSrcForTier(
  tier: GamificationLevelBadgeTier | null | undefined,
): string | null {
  if (!tier) return null;
  return GAMIFICATION_BADGE_ASSETS[tier] ?? null;
}

/** UI label for a level number (Bronze / Argent / Gold). */
export function displayLabelForLevelNumber(levelNumber: number | null | undefined): string | null {
  const tier = tierFromLevelNumber(levelNumber);
  if (!tier) return null;
  return GAMIFICATION_TIER_LABELS[tier];
}

/** Remote icon URL if present; otherwise the bundled image for the resolved tier. */
export function resolveLevelBadgeImageSrc(input: {
  levelIcon?: string | null;
  levelBadge?: GamificationLevelBadge | null;
  levelNumber?: number | null;
}): string | null {
  if (isRemoteIconUrl(input.levelIcon)) return input.levelIcon.trim();
  const tier =
    input.levelBadge?.tier ??
    tierFromLevelNumber(input.levelNumber ?? input.levelBadge?.niveauNumero);
  return badgeImageSrcForTier(tier);
}

/** Show a badge when the API sent one, the level maps to a tier, or the icon is a remote URL. */
export function shouldShowLevelBadge(input: {
  levelBadge?: GamificationLevelBadge | null;
  levelNumber?: number | null;
  levelIcon?: string | null;
}): boolean {
  return (
    Boolean(input.levelBadge) ||
    tierFromLevelNumber(input.levelNumber) != null ||
    isRemoteIconUrl(input.levelIcon)
  );
}

/** Badge title: API display label, then derived tier, then the level name. */
export function resolveLevelDisplayLabel(input: {
  badgeLabel?: string | null;
  levelNumber?: number | null;
  levelName?: string | null;
}): string | null {
  return (
    input.badgeLabel ?? displayLabelForLevelNumber(input.levelNumber) ?? input.levelName ?? null
  );
}

/** True when the progress bar toward the next level can be drawn. */
export function hasNextLevelProgress(input: {
  nextNiveau: unknown;
  xpToNextLevel: number | null;
  xpRangeToNext: number | null;
}): boolean {
  return input.nextNiveau != null && input.xpToNextLevel != null && (input.xpRangeToNext ?? 0) > 0;
}

/** Progress-bar width, capped at 100. */
export function xpProgressPercent(xpInCurrentLevel: number, xpRangeToNext: number): number {
  return Math.min(100, (xpInCurrentLevel / xpRangeToNext) * 100);
}

/** Map HTTP status to a staff-facing progression error (403 / 404 / generic). */
export function gamificationErrorMessage(status: number | undefined): string {
  if (status === 403) return getErrorMessage('progressUnavailable');
  if (status === 404) return getErrorMessage('userNotFound');
  return getErrorMessage('loadProgressFailed');
}

/** Tutor-count subtitle; empty when the establishment has no tutors. */
export function formatTutorCountLabel(count: number): string | null {
  if (count <= 0) return null;
  const label = count > 1 ? getRoleLabelPlural('tutor') : getRoleLabel('tutor');
  return `${count} ${label}`;
}

/** City and tutor-count joined for the establishment row subtitle. */
export function establishmentMetaLabel(
  city: string | null | undefined,
  tutorCount: number,
): string {
  return [city, formatTutorCountLabel(tutorCount)].filter(Boolean).join(' · ');
}

/** Shared row classes for a highlighted or compact leaderboard line. */
export function leaderboardRowClassName(compact: boolean, isHighlighted: boolean): string {
  return cn(
    LEADERBOARD_ROW_CLASS,
    compact ? 'py-2.5' : 'py-3',
    isHighlighted ? LEADERBOARD_ROW_HIGHLIGHT_CLASS : 'border-border',
  );
}

/** Establishment icon box class for compact vs default rows. */
export function establishmentIconBoxClass(compact: boolean): string {
  return compact ? ESTABLISHMENT_ICON_BOX_CLASS.compact : ESTABLISHMENT_ICON_BOX_CLASS.default;
}

/** Establishment icon glyph class for compact vs default rows. */
export function establishmentIconClass(compact: boolean): string {
  return compact ? ESTABLISHMENT_ICON_CLASS.compact : ESTABLISHMENT_ICON_CLASS.default;
}

/** Whether `rank` is a podium place (1, 2, or 3). */
export function isPodiumRank(rank: number): rank is PodiumRank {
  return rank === 1 || rank === 2 || rank === 3;
}

/** Trophy for first place, medal otherwise. */
export function rankBadgeIcon(rank: PodiumRank): LucideIcon {
  if (rank === 1) return Trophy;
  return Medal;
}

/** Accessible name for a rank chip. */
export function rankAriaLabel(rank: number): string {
  return `Rang ${rank}`;
}

/** Podium ranks that actually have card content, in visual order. */
export function visiblePodiumRanks(slots: Partial<Record<PodiumRank, unknown>>): PodiumRank[] {
  return PODIUM_ORDER.filter((rank) => slots[rank] != null);
}

/** Grid column class for 1, 2, or 3 filled podium cards. */
export function podiumColumnClass(filledCount: number): string {
  if (filledCount === 1) return PODIUM_COLUMN_CLASS[1];
  if (filledCount === 2) return PODIUM_COLUMN_CLASS[2];
  return PODIUM_COLUMN_CLASS[3];
}

/** Podium on page 1 when the search box is empty. */
export function shouldShowLeaderboardPodium(page: number, search: string): boolean {
  return page === 1 && search.length === 0;
}

/** Top 3 go on the podium; ranks 4+ stay in the list. */
export function splitPodiumAndRest<T extends { rank: number }>(
  entries: T[],
): { podium: T[]; rest: T[] } {
  return {
    podium: entries.filter((entry) => entry.rank >= 1 && entry.rank <= 3),
    rest: entries.filter((entry) => entry.rank > 3),
  };
}

/** Map ranked entries (1–3) onto podium slots; first entry wins if ranks collide. */
export function buildPodiumSlots<T extends { rank: number }>(
  podiumEntries: T[],
  renderContent: (entry: T) => React.ReactNode,
): Partial<Record<PodiumRank, React.ReactNode>> {
  const slots: Partial<Record<PodiumRank, React.ReactNode>> = {};
  for (const entry of podiumEntries) {
    if (!isPodiumRank(entry.rank) || slots[entry.rank] != null) continue;
    slots[entry.rank] = renderContent(entry);
  }
  return slots;
}

/** Pagination window; `null` when the result set is empty. */
export function leaderboardPageRange(
  page: number,
  pageSize: number,
  total: number,
): { totalPages: number; startIndex: number; endIndex: number } | null {
  if (total <= 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = (page - 1) * pageSize;
  return { totalPages, startIndex, endIndex: startIndex + pageSize };
}
